// frontend/src/app/components/truth-set/site-read-panel.tsx
//
// Stage 3 admin panel for the SITE READ (truth set, part 1; backend
// app/services/truth_set). The platform reads the brand's own domain and any
// uploaded documents so a later judge pass can PROPOSE facts for a person to
// confirm. Nothing on this panel is a fact yet: it is the source material.
//
// The read is bounded (page cap, time cap), resumable, and cancelled with a
// toggle. The first read is the baseline; later reads (on demand, and monthly
// once a brand has had one) record every page that appeared, changed or went
// away, with the diff, in the site's changes log.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronDown, ChevronUp, FileText, Globe, Loader2, Pause, Play, RotateCcw, Settings2, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";

import {
  STOP_LABELS, cancelSiteRead, getSiteChange, getSiteOverview, getSiteRead, launchSiteRead,
  removeTruthDocument, resumeSiteRead, updateSiteConfig, uploadTruthDocument,
  type SiteChange, type SiteConfig, type SiteOverview, type SiteRead, type SiteReadDetail,
} from "@/api/truth-set";

const ACCENT = "var(--spectral-violet)";
const POLL_MS = 4000;

const box: React.CSSProperties = {
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 8,
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
  letterSpacing: "0.04em", color: "var(--muted-foreground)", marginBottom: 4,
};
const input: React.CSSProperties = {
  width: "100%", padding: "6px 10px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 6, outline: "none",
};
const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const btn = (primary = false, disabled = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6,
  fontSize: "var(--text-xs)", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  border: primary ? "none" : "1px solid var(--border)",
  backgroundColor: primary ? ACCENT : "transparent",
  color: primary ? "white" : "var(--foreground)",
});

function when(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function minutes(sec: number): string {
  return sec < 60 ? `${Math.round(sec)}s` : `${Math.round(sec / 60)} min`;
}

const isLive = (r: SiteRead) => r.status === "pending" || r.status === "running";

/** onSourcesChanged: called whenever the panel reloads (after an upload, a
 *  removal, and on every poll of a running read), so a screen that counts
 *  the brand's sources (Stage 2's Generate) can reload its own numbers. */
export function SiteReadPanel({ customerId, onSourcesChanged }: { customerId: number; onSourcesChanged?: () => void }) {
  const [overview, setOverview] = useState<SiteOverview | null>(null);
  const [live, setLive] = useState<SiteReadDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<SiteConfig>>({});
  const [expandedRead, setExpandedRead] = useState<number | null>(null);
  const [readDetail, setReadDetail] = useState<SiteReadDetail | null>(null);
  const [diff, setDiff] = useState<SiteChange | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Read through a ref so `refresh` stays stable when the parent re-renders.
  const sourcesChanged = useRef(onSourcesChanged);
  sourcesChanged.current = onSourcesChanged;

  const refresh = useCallback(async () => {
    try {
      const next = await getSiteOverview(customerId);
      setOverview(next);
      sourcesChanged.current?.();
      const running = (next.reads || []).find(isLive);
      setLive(running ? await getSiteRead(running.id) : null);
    } catch (e: any) {
      toast.error(e?.message || "Could not load the site read.");
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, refresh]);

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) toast.success(done);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  const openRead = async (readId: number) => {
    if (expandedRead === readId) {
      setExpandedRead(null);
      return;
    }
    setExpandedRead(readId);
    setReadDetail(null);
    try {
      setReadDetail(await getSiteRead(readId));
    } catch (e: any) {
      toast.error(e?.message || "Could not load that read.");
    }
  };

  const openDiff = async (changeId: number) => {
    try {
      setDiff(await getSiteChange(changeId));
    } catch (e: any) {
      toast.error(e?.message || "Could not load the diff.");
    }
  };

  if (!overview) {
    return (
      <div style={{ ...box, padding: 16, marginBottom: 16 }}>
        <span style={muted}><Loader2 className="inline w-3 h-3 animate-spin" /> Loading the site read…</span>
      </div>
    );
  }
  if (!overview.available) {
    return (
      <div style={{ ...box, padding: 16, marginBottom: 16 }}>
        <span style={muted}>
          The site read is not available yet: the truth-set migration has not been applied.
        </span>
      </div>
    );
  }

  const config = overview.config!;
  const reads = overview.reads || [];
  const documents = overview.documents || [];
  const changes = overview.changes || [];
  const lastDone = reads.find((r) => r.status === "completed");

  return (
    <div style={{ ...box, padding: 16, marginBottom: 16 }}>
      <button
        type="button" onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none",
                 cursor: "pointer", padding: 0, color: "var(--foreground)" }}
      >
        <Globe className="w-4 h-4" style={{ color: ACCENT }} />
        <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Site read</span>
        <span style={{ ...muted, marginLeft: 8 }}>
          {overview.pages?.live ?? 0} pages stored · {documents.length} document{documents.length === 1 ? "" : "s"}
          {lastDone ? ` · last read ${when(lastDone.finished_at)}` : " · never read"}
        </span>
        <span style={{ marginLeft: "auto" }}>{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ ...muted, lineHeight: 1.5 }}>
            Reads the brand's own site and the documents below, so facts can be proposed for confirmation.
            Nothing here is treated as true until a person confirms it.
          </p>

          {/* Settings */}
          <div style={{ ...box, padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{config.root_url || "No website address"}</span>
              <span style={muted}>
                up to {config.page_cap} pages · {minutes(config.time_cap_sec)} · {config.delay_sec}s between requests ·{" "}
                {overview.scheduled ? `re-read every ${config.cadence_days} days` : "not scheduled"}
              </span>
              <button type="button" style={{ ...btn(), marginLeft: "auto" }} disabled={busy}
                      onClick={() => { setDraft({ ...config }); setEditing(!editing); }}>
                <Settings2 className="w-3 h-3" /> Settings
              </button>
            </div>
            {config.root_error && <p style={{ ...muted, color: "var(--infrared-coral)", marginTop: 6 }}>{config.root_error}</p>}
            {editing && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 12 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <span style={label}>Website</span>
                  <input style={input} value={draft.root_url ?? ""} placeholder="brand.com"
                         onChange={(e) => setDraft({ ...draft, root_url: e.target.value })} />
                </div>
                {([["page_cap", "Page cap"], ["time_cap_sec", "Time cap (seconds)"], ["delay_sec", "Delay (seconds)"],
                   ["cadence_days", "Re-read every (days)"]] as const).map(([key, text]) => (
                  <div key={key}>
                    <span style={label}>{text}</span>
                    <input style={input} type="number" value={(draft[key] as number | undefined) ?? ""}
                           onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })} />
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1" }}>
                  <span style={label}>Skip URLs containing (one per line)</span>
                  <textarea style={{ ...input, minHeight: 56 }} value={(draft.exclude_patterns || []).join("\n")}
                            onChange={(e) => setDraft({ ...draft, exclude_patterns: e.target.value.split("\n") })} />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-xs)" }}>
                  <input type="checkbox" checked={!!draft.schedule_enabled}
                         onChange={(e) => setDraft({ ...draft, schedule_enabled: e.target.checked })} />
                  Monthly re-read
                </label>
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
                  <button type="button" style={btn(true, busy)} disabled={busy}
                          onClick={() => act(async () => {
                            await updateSiteConfig(customerId, {
                              ...draft, exclude_patterns: (draft.exclude_patterns || []).filter((p) => p.trim()),
                            });
                            setEditing(false);
                          }, "Settings saved")}>
                    Save
                  </button>
                  <button type="button" style={btn()} onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* The live read, or the launch button */}
          {live ? (
            <div style={{ ...box, padding: 12, borderColor: ACCENT }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: ACCENT }} />
                <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                  {live.cancel_requested ? "Cancelling…" : live.status === "pending" ? "Starting…" : "Reading the site"}
                </span>
                <span style={muted}>
                  {live.pages_fetched} of up to {live.config_json?.page_cap ?? config.page_cap} pages ·{" "}
                  {live.live_counts.urls.queued ?? 0} queued · {minutes(live.elapsed_sec)}
                </span>
                <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-xs)" }}>
                  <input type="checkbox" checked={live.cancel_requested} disabled={busy}
                         onChange={(e) => act(() => cancelSiteRead(live.id, e.target.checked))} />
                  Cancel
                </label>
              </div>
            </div>
          ) : (
            <div>
              <button type="button" style={btn(true, busy || !config.root_url)} disabled={busy || !config.root_url}
                      onClick={() => act(() => launchSiteRead(customerId), "Site read started")}>
                <Play className="w-3 h-3" /> {lastDone ? "Read the site again" : "Read the site"}
              </button>
            </div>
          )}

          {/* History */}
          {reads.length > 0 && (
            <div>
              <span style={label}>Reads</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {reads.map((read) => (
                  <div key={read.id} style={{ ...box, padding: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", cursor: "pointer" }}
                         onClick={() => openRead(read.id)}>
                      <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{when(read.created_at)}</span>
                      <span style={muted}>
                        {read.trigger} · {read.status}
                        {read.stop_reason ? ` · ${STOP_LABELS[read.stop_reason]}` : ""}
                        {` · ${read.pages_fetched} pages`}
                        {read.is_baseline ? " · baseline" :
                          ` · ${read.pages_new} new, ${read.pages_changed} changed, ${read.pages_gone} gone`}
                      </span>
                      {!isLive(read) && read.stop_reason && read.stop_reason !== "done" && !live && (
                        <button type="button" style={{ ...btn(false, busy), marginLeft: "auto" }} disabled={busy}
                                onClick={(e) => { e.stopPropagation(); act(() => resumeSiteRead(read.id), "Resumed"); }}>
                          <RotateCcw className="w-3 h-3" /> Resume
                        </button>
                      )}
                    </div>
                    {read.error_message && <p style={{ ...muted, color: "var(--infrared-coral)", marginTop: 4 }}>{read.error_message}</p>}
                    {expandedRead === read.id && (
                      <ReadReport detail={readDetail} onDiff={openDiff} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Site changes log */}
          {changes.length > 0 && (
            <div>
              <span style={label}>Site changes</span>
              <ChangeList changes={changes} onDiff={openDiff} />
            </div>
          )}

          {/* Documents */}
          <div>
            <span style={label}>Documents</span>
            <p style={{ ...muted, marginBottom: 6 }}>
              Rate sheets, FAQs, disclosures. PDF, Word, text, Markdown, CSV or HTML, up to 15 MB.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {documents.map((doc) => (
                <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-xs)" }}>
                  <FileText className="w-3 h-3" />
                  <span style={{ fontWeight: 600 }}>{doc.filename}</span>
                  <span style={muted}>{Math.round(doc.size_bytes / 1024)} KB · {doc.text_chars.toLocaleString()} characters read · {when(doc.uploaded_at)}</span>
                  <button type="button" style={{ ...btn(false, busy), marginLeft: "auto" }} disabled={busy}
                          onClick={() => act(() => removeTruthDocument(doc.id), "Document removed")}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.csv,.html,.htm"
                   onChange={(e) => {
                     const file = e.target.files?.[0];
                     e.target.value = "";
                     if (file) act(async () => {
                       const doc = await uploadTruthDocument(customerId, file);
                       if (doc.duplicate) toast.info(`${doc.filename} is already uploaded.`);
                     }, "Document uploaded");
                   }} />
            <button type="button" style={{ ...btn(false, busy), marginTop: 6 }} disabled={busy}
                    onClick={() => fileRef.current?.click()}>
              <Upload className="w-3 h-3" /> Upload a document
            </button>
          </div>
        </div>
      )}

      {diff && (
        <div style={{ ...box, padding: 12, marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, wordBreak: "break-all" }}>{diff.url}</span>
            <button type="button" style={{ ...btn(), marginLeft: "auto" }} onClick={() => setDiff(null)}>Close</button>
          </div>
          <pre style={{ fontSize: 11, lineHeight: 1.45, maxHeight: 360, overflow: "auto", marginTop: 8, whiteSpace: "pre-wrap" }}>
            {(diff.diff || "").split("\n").map((line, i) => (
              <div key={i} style={{
                color: line.startsWith("+") ? "var(--signal-green, green)" :
                  line.startsWith("-") ? "var(--infrared-coral)" : "var(--foreground)",
              }}>{line}</div>
            ))}
          </pre>
          {diff.diff_truncated && <p style={muted}>The diff is longer than shown.</p>}
        </div>
      )}
    </div>
  );
}

function ChangeList({ changes, onDiff }: { changes: SiteChange[]; onDiff: (id: number) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 220, overflow: "auto" }}>
      {changes.map((change) => (
        <div key={change.id} style={{ display: "flex", gap: 8, fontSize: "var(--text-xs)", alignItems: "baseline" }}>
          <span style={{ minWidth: 56, fontWeight: 600 }}>{change.kind}</span>
          <span style={{ wordBreak: "break-all" }}>{change.url}</span>
          {change.kind === "changed" && (
            <button type="button" style={{ ...btn(), marginLeft: "auto", padding: "1px 8px" }}
                    onClick={() => onDiff(change.id)}>
              +{change.lines_added} −{change.lines_removed}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ReadReport({ detail, onDiff }: { detail: SiteReadDetail | null; onDiff: (id: number) => void }) {
  if (!detail) return <p style={{ ...muted, marginTop: 8 }}><Loader2 className="inline w-3 h-3 animate-spin" /> Loading…</p>;
  const report = detail.report_json;
  const urls = report?.urls || detail.live_counts.urls;
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ ...muted, display: "flex", gap: 12, flexWrap: "wrap" }}>
        {Object.entries(urls).map(([status, n]) => <span key={status}>{status}: {n}</span>)}
        {report && <span>{minutes(report.elapsed_sec)} of {minutes(report.time_cap_sec)}</span>}
      </div>
      {report?.notes?.length ? (
        <ul style={{ ...muted, paddingLeft: 16, listStyle: "disc" }}>
          {report.notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      ) : null}
      {report && (["errors", "blocked", "skipped"] as const).map((kind) =>
        report.samples?.[kind]?.length ? (
          <div key={kind}>
            <span style={label}>{kind}</span>
            {report.samples[kind].map((row) => (
              <div key={row.id} style={{ fontSize: 11, wordBreak: "break-all" }}>
                {row.url} <span style={muted}>{row.reason}</span>
              </div>
            ))}
          </div>
        ) : null)}
      {detail.changes.length > 0 && <ChangeList changes={detail.changes} onDiff={onDiff} />}
      {detail.status === "running" && <span style={muted}><Pause className="inline w-3 h-3" /> Still running</span>}
    </div>
  );
}
